import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  ActivityIndicator,
  RefreshControl,
  Platform,
} from 'react-native';
import { ChannelCard, ChannelCardProps } from './ChannelCard';
import { colors, typography, spacing } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface ChannelListProps {
  channels: Array<{
    channel: string;
    name: string;
    isConnected: boolean;
  }>;
  bindings?: Array<{
    channel: string;
    syncStatus: 'pending' | 'synced' | 'error' | 'never_synced' | 'syncing';
    lastSyncedAt?: Date | string;
    externalCatalogId?: string;
  }>;
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  onChannelPress?: (channel: string) => void;
  onBind?: (channel: string) => void;
  onUnbind?: (channel: string) => void;
  emptyMessage?: string;
  containerStyle?: any;
}

export const ChannelList: React.FC<ChannelListProps> = ({
  channels,
  bindings = [],
  loading = false,
  refreshing = false,
  onRefresh,
  onChannelPress,
  onBind,
  onUnbind,
  emptyMessage = 'No channels available',
  containerStyle,
}) => {
  const styles = useThemedStyles(getStyles);
  const getBindingForChannel = (channel: string) => {
    return bindings.find(b => b.channel === channel);
  };

  const renderChannel = ({ item }: { item: { channel: string; name: string; isConnected: boolean } }) => {
    const binding = getBindingForChannel(item.channel);
    
    return (
      <ChannelCard
        channel={item}
        binding={binding}
        onPress={onChannelPress}
        onBind={onBind}
        onUnbind={onUnbind}
      />
    );
  };

  if (loading && channels.length === 0) {
    return (
      <View style={[styles.centerContainer, containerStyle]}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={styles.centerText}>Loading channels...</Text>
      </View>
    );
  }

  if (channels.length === 0) {
    return (
      <View style={[styles.centerContainer, containerStyle]}>
        <Text style={styles.emptyText}>{emptyMessage}</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, containerStyle]}>
      <FlatList
        data={channels}
        renderItem={renderChannel}
        keyExtractor={(item) => item.channel}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          ) : undefined
        }
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  centerText: {
    marginTop: spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    textAlign: 'center',
  },
});

